import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { useLanguage } from "../contexts/LanguageContext";
import logger from "../utils/logger.js";
import {
  completeCourseLesson,
  findLessonById,
  flattenCourseLessons,
  formatLessonDuration,
  getCourseDescription,
  getCourseLearnPayload,
  getCourseLesson,
  getCourseTitle,
  getEmbedVideoUrl,
  getLessonTitle,
  isDirectVideoFile,
  resolveCourseId,
  saveCourseLessonProgress,
} from "../api/courses";

function updateLessonInSections(course, lessonId, updater) {
  if (!course) return course;

  return {
    ...course,
    sections: (course.sections || []).map((section) => ({
      ...section,
      lessons: (section.lessons || []).map((lesson) =>
        Number(lesson.id) === Number(lessonId) ? updater(lesson) : lesson
      ),
    })),
  };
}

function renderLessonText(content) {
  const html = String(content || "").trim();
  if (!html) {
    return null;
  }

  return (
    <div
      className="course-rich-text"
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
    />
  );
}

export default function CourseLearn() {
  const { courseRef, lessonId } = useParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  const [resolvedCourseId, setResolvedCourseId] = useState(null);
  const [coursePayload, setCoursePayload] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lessonLoading, setLessonLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const videoRef = useRef(null);
  const lastSavedRef = useRef(0);

  const course = coursePayload?.course || null;
  const flattenedLessons = useMemo(() => flattenCourseLessons(course), [course]);

  useEffect(() => {
    let active = true;

    async function loadCourse() {
      try {
        setLoading(true);
        setError("");
        setNotice("");

        const courseId = await resolveCourseId(courseRef);
        if (!active) return;

        setResolvedCourseId(courseId);

        const payload = await getCourseLearnPayload(courseId);
        if (!active) return;

        setCoursePayload(payload);

        const targetLessonId =
          Number(lessonId) ||
          Number(payload?.current_lesson_id) ||
          Number(flattenCourseLessons(payload?.course)?.[0]?.id) ||
          null;

        if (!targetLessonId) {
          setCurrentLesson(null);
          return;
        }

        const lessonData = await getCourseLesson(courseId, targetLessonId);
        if (!active) return;

        setCurrentLesson(lessonData?.lesson || null);
      } catch (err) {
        logger.error("Course learn load error", err);
        if (!active) return;
        setError(
          err?.status === 403
            ? isAr
              ? "يجب أن تكون مسجلًا في هذا الكورس أولًا."
              : "You need to enroll in this course first."
            : isAr
              ? "تعذر تحميل صفحة التعلم."
              : "Unable to load learning page."
        );
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadCourse();

    return () => {
      active = false;
    };
  }, [courseRef, lessonId, isAr]);

  const currentLessonId = Number(lessonId || currentLesson?.id || 0);
  const fallbackLesson = useMemo(
    () => findLessonById(course, currentLessonId),
    [course, currentLessonId]
  );
  const activeLesson = currentLesson || fallbackLesson;
  const activeLessonTitle = getLessonTitle(activeLesson, isAr);
  const courseTitle = getCourseTitle(course, isAr);
  const courseUrlRef = course?.slug || course?.id || courseRef;

  const currentIndex = flattenedLessons.findIndex(
    (lesson) => Number(lesson.id) === Number(activeLesson?.id)
  );
  const previousLesson = currentIndex > 0 ? flattenedLessons[currentIndex - 1] : null;
  const nextLesson = currentIndex >= 0 ? flattenedLessons[currentIndex + 1] || null : null;

  const embedUrl = getEmbedVideoUrl(activeLesson?.video_url);
  const directVideo = isDirectVideoFile(activeLesson?.video_url);
  const textContent = isAr
    ? activeLesson?.content_ar || activeLesson?.content_en || ""
    : activeLesson?.content_en || activeLesson?.content_ar || "";

  useEffect(() => {
    lastSavedRef.current = Number(activeLesson?.progress?.last_position_seconds || 0);
  }, [activeLesson?.id, activeLesson?.progress?.last_position_seconds]);

  const navigateToLesson = (targetLessonId) => {
    if (!targetLessonId) return;
    navigate(`/learn/${courseUrlRef}/lesson/${targetLessonId}`);
  };

  const syncLessonProgress = (lessonData, enrollmentData = null) => {
    setCurrentLesson((prev) =>
      prev && Number(prev.id) === Number(lessonData?.id) ? lessonData : prev
    );

    if (lessonData?.id) {
      setCoursePayload((prev) => {
        if (!prev?.course) return prev;

        const nextCourse = updateLessonInSections(prev.course, lessonData.id, (lesson) => ({
          ...lesson,
          progress: {
            ...lesson.progress,
            ...lessonData.progress,
          },
        }));

        return {
          ...prev,
          course: enrollmentData
            ? {
                ...nextCourse,
                enrollment: {
                  ...(nextCourse.enrollment || {}),
                  ...enrollmentData,
                },
              }
            : nextCourse,
        };
      });
    }
  };

  const handleSaveProgress = async (position = null, silent = false) => {
    if (!resolvedCourseId || !activeLesson?.id) return;

    try {
      if (!silent) {
        setSaving(true);
        setNotice("");
      }

      const safePosition =
        position !== null ? Number(Math.max(0, Math.floor(position))) : null;

      const response = await saveCourseLessonProgress(resolvedCourseId, activeLesson.id, {
        last_position_seconds: safePosition,
      });

      const enrollmentData = response?.data?.enrollment || null;
      syncLessonProgress(
        {
          ...activeLesson,
          progress: {
            ...(activeLesson.progress || {}),
            last_position_seconds: safePosition,
          },
        },
        enrollmentData
      );

      if (!silent) {
        setNotice(isAr ? "تم حفظ التقدم." : "Progress saved.");
      }
    } catch (err) {
      logger.error("Save progress error", err);
      if (!silent) {
        setError(isAr ? "تعذر حفظ التقدم." : "Unable to save progress.");
      }
    } finally {
      if (!silent) {
        setSaving(false);
      }
    }
  };

  const handleCompleteLesson = async () => {
    if (!resolvedCourseId || !activeLesson?.id) return;

    try {
      setSaving(true);
      setError("");
      setNotice("");

      const response = await completeCourseLesson(resolvedCourseId, activeLesson.id);
      const progressData = response?.data?.progress || {};
      const enrollmentData = response?.data?.enrollment || null;

      syncLessonProgress(
        {
          ...activeLesson,
          progress: {
            ...(activeLesson.progress || {}),
            ...progressData,
          },
        },
        enrollmentData
      );

      setNotice(isAr ? "تم تعليم الدرس كمكتمل." : "Lesson marked as complete.");
    } catch (err) {
      logger.error("Complete lesson error", err);
      setError(isAr ? "تعذر تحديث حالة الدرس." : "Unable to update lesson status.");
    } finally {
      setSaving(false);
    }
  };

  const handleVideoTimeUpdate = async () => {
    const video = videoRef.current;
    if (!video || !resolvedCourseId || !activeLesson?.id) return;

    const currentSeconds = Math.floor(video.currentTime || 0);
    const lastSaved = Number(lastSavedRef.current || 0);

    if (Math.abs(currentSeconds - lastSaved) < 10) {
      return;
    }

    lastSavedRef.current = currentSeconds;
    await handleSaveProgress(currentSeconds, true);
  };

  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    const lastPosition = Number(activeLesson?.progress?.last_position_seconds || 0);

    if (video && lastPosition > 0 && lastPosition < Number(video.duration || Infinity)) {
      video.currentTime = lastPosition;
    }
  };

  const handleSelectLesson = async (targetLesson) => {
    if (!resolvedCourseId || !targetLesson?.id) return;

    try {
      setLessonLoading(true);
      setError("");
      setNotice("");

      const lessonPayload = await getCourseLesson(resolvedCourseId, targetLesson.id);
      setCurrentLesson(lessonPayload?.lesson || null);
      navigateToLesson(targetLesson.id);
    } catch (err) {
      logger.error("Lesson load error", err);
      setError(isAr ? "تعذر فتح الدرس." : "Unable to open lesson.");
    } finally {
      setLessonLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary mb-3" role="status" />
        <p className="mb-0">{isAr ? "جارِ تحميل مساحة التعلم..." : "Loading learning space..."}</p>
      </div>
    );
  }

  if (error && !course) {
    return (
      <div className="container py-5" dir={isAr ? "rtl" : "ltr"}>
        <div className="alert alert-danger rounded-4 mb-3">{error}</div>
        <Link to={`/courses/${courseRef}`} className="btn btn-outline-dark rounded-pill">
          {isAr ? "العودة للكورس" : "Back to course"}
        </Link>
      </div>
    );
  }

  return (
    <div className="container-fluid py-3 py-lg-4" dir={isAr ? "rtl" : "ltr"}>
      <div className="container-xxl">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
          <div>
            <div className="text-muted small mb-1">
              <Link to="/courses" className="text-reset text-decoration-none">
                {isAr ? "الكورسات" : "Courses"}
              </Link>
              <span className="mx-2">/</span>
              <Link
                to={`/courses/${courseUrlRef}`}
                className="text-reset text-decoration-none"
              >
                {courseTitle}
              </Link>
            </div>
            <h1 className="h3 fw-bold mb-0">{activeLessonTitle || courseTitle}</h1>
          </div>

          <div className="d-flex flex-wrap gap-2">
            <Link to="/my-courses" className="btn btn-outline-dark rounded-pill">
              {isAr ? "كورساتي" : "My Courses"}
            </Link>
            <button
              type="button"
              className="btn btn-outline-primary rounded-pill"
              onClick={() =>
                handleSaveProgress(
                  directVideo && videoRef.current
                    ? Math.floor(videoRef.current.currentTime || 0)
                    : Number(activeLesson?.progress?.last_position_seconds || 0)
                )
              }
              disabled={saving || !activeLesson}
            >
              {saving ? (isAr ? "جارِ الحفظ..." : "Saving...") : isAr ? "حفظ التقدم" : "Save Progress"}
            </button>
          </div>
        </div>

        {notice ? <div className="alert alert-success rounded-4">{notice}</div> : null}
        {error && course ? <div className="alert alert-danger rounded-4">{error}</div> : null}

        <div className="row g-4">
          <div className="col-xl-4">
            <aside className="card border-0 shadow-sm rounded-4 course-learn-sidebar">
              <div className="card-body p-3 p-lg-4">
                <div className="mb-3">
                  <h2 className="h5 fw-bold mb-1">{courseTitle}</h2>
                  <p className="text-muted small mb-0">
                    {course?.enrollment?.progress_percent || 0}% {isAr ? "مكتمل" : "completed"}
                  </p>
                </div>

                <div className="progress rounded-pill mb-4" style={{ height: 10 }}>
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{ width: `${Math.max(0, Math.min(100, Number(course?.enrollment?.progress_percent || 0)))}%` }}
                    aria-valuenow={Number(course?.enrollment?.progress_percent || 0)}
                    aria-valuemin="0"
                    aria-valuemax="100"
                  />
                </div>

                <div className="course-sidebar-sections">
                  {(course?.sections || []).map((section) => (
                    <div className="course-sidebar-section" key={section.id}>
                      <div className="course-sidebar-section__title">
                        {isAr ? section.title_ar || section.title_en : section.title_en || section.title_ar}
                      </div>
                      <div className="list-group list-group-flush">
                        {(section.lessons || []).map((lesson) => {
                          const isActive = Number(activeLesson?.id) === Number(lesson.id);
                          const isCompleted = Boolean(lesson?.progress?.is_completed);

                          return (
                            <button
                              key={lesson.id}
                              type="button"
                              className={`list-group-item list-group-item-action border-0 rounded-3 course-lesson-item ${isActive ? "active" : ""}`}
                              onClick={() => handleSelectLesson(lesson)}
                              disabled={lessonLoading && isActive}
                            >
                              <div className="d-flex align-items-start justify-content-between gap-3">
                                <div>
                                  <div className="fw-semibold mb-1">
                                    {getLessonTitle(lesson, isAr)}
                                  </div>
                                  <div className="text-muted small">
                                    {formatLessonDuration(lesson?.duration_seconds) ||
                                      (isAr ? "درس" : "Lesson")}
                                  </div>
                                </div>
                                <span
                                  className={`badge ${isCompleted ? "text-bg-success" : "text-bg-light"}`}
                                >
                                  {isCompleted ? (isAr ? "مكتمل" : "Done") : isAr ? "جديد" : "New"}
                                </span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </aside>
          </div>

          <div className="col-xl-8">
            <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
              <div className="card-body p-3 p-lg-4">
                {lessonLoading ? (
                  <div className="py-5 text-center">
                    <div className="spinner-border text-primary mb-3" role="status" />
                    <p className="mb-0">{isAr ? "جارِ تحميل الدرس..." : "Loading lesson..."}</p>
                  </div>
                ) : activeLesson ? (
                  <>
                    <div className="course-player-shell rounded-4 overflow-hidden mb-4">
                      {directVideo ? (
                        <video
                          ref={videoRef}
                          src={activeLesson.video_url}
                          controls
                          className="course-video-player"
                          onTimeUpdate={handleVideoTimeUpdate}
                          onLoadedMetadata={handleLoadedMetadata}
                        />
                      ) : embedUrl ? (
                        <iframe
                          src={embedUrl}
                          title={activeLessonTitle}
                          className="course-video-embed"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ) : textContent ? (
                        <div className="course-player-placeholder">
                          <div>
                            <div className="course-player-placeholder__icon">T</div>
                            <div className="fw-semibold">{isAr ? "درس نصي" : "Text lesson"}</div>
                          </div>
                        </div>
                      ) : (
                        <div className="course-player-placeholder">
                          <div>
                            <div className="course-player-placeholder__icon">?</div>
                            <div className="fw-semibold">
                              {isAr ? "محتوى الدرس سيضاف قريبًا" : "Lesson content will be added soon"}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
                      <div>
                        <h2 className="h4 fw-bold mb-1">{activeLessonTitle}</h2>
                        <div className="text-muted small">
                          {formatLessonDuration(activeLesson?.duration_seconds) ||
                            (isAr ? "مدة مرنة" : "Flexible duration")}
                        </div>
                      </div>

                      <button
                        type="button"
                        className={`btn rounded-pill px-4 ${activeLesson?.progress?.is_completed ? "btn-success" : "btn-outline-success"}`}
                        onClick={handleCompleteLesson}
                        disabled={saving}
                      >
                        {activeLesson?.progress?.is_completed
                          ? isAr
                            ? "تم الإكمال"
                            : "Completed"
                          : isAr
                            ? "تعليم كمكتمل"
                            : "Mark Complete"}
                      </button>
                    </div>

                    {renderLessonText(textContent) ? (
                      <div className="card border-0 bg-light rounded-4 mb-4">
                        <div className="card-body p-4">
                          {renderLessonText(textContent)}
                        </div>
                      </div>
                    ) : null}

                    {activeLesson?.attachment_path ? (
                      <div className="card border-0 bg-light rounded-4 mb-4">
                        <div className="card-body d-flex flex-wrap align-items-center justify-content-between gap-3">
                          <div>
                            <h3 className="h6 fw-bold mb-1">
                              {isAr ? "ملف مرفق" : "Attachment"}
                            </h3>
                            <p className="text-muted small mb-0">
                              {isAr ? "يمكنك فتح أو تحميل ملف الدرس." : "You can open or download the lesson file."}
                            </p>
                          </div>
                          <a
                            href={activeLesson.attachment_path}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-outline-dark rounded-pill"
                          >
                            {isAr ? "فتح الملف" : "Open File"}
                          </a>
                        </div>
                      </div>
                    ) : null}

                    <div className="course-lesson-navigation d-flex flex-wrap justify-content-between gap-3">
                      <button
                        type="button"
                        className="btn btn-outline-dark rounded-pill px-4"
                        onClick={() => navigateToLesson(previousLesson?.id)}
                        disabled={!previousLesson}
                      >
                        {isAr ? "السابق" : "Previous"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary rounded-pill px-4"
                        onClick={() => navigateToLesson(nextLesson?.id)}
                        disabled={!nextLesson}
                      >
                        {isAr ? "التالي" : "Next"}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="py-5 text-center">
                    <h2 className="h5 fw-bold mb-2">
                      {isAr ? "لا يوجد درس متاح حاليًا" : "No lesson is currently available"}
                    </h2>
                    <p className="text-muted mb-0">
                      {getCourseDescription(course, isAr) ||
                        (isAr ? "سيظهر محتوى الدروس هنا عند توفره." : "Lesson content will appear here when available.")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
